import pluralize from 'pluralize'

import { hasStatementSegmentFailed } from '@/modules/projects/status'
import type {
  PrintedPages,
  Statement,
  StatementSegment,
} from '@/modules/projects/stores'

export interface PageRange {
  start: number
  end: number
}

export const pageSpanLabel = (
  startPage: number | null,
  endPage: number | null,
) => {
  if (startPage == null) return 'Unknown pages'
  if (endPage == null || endPage === startPage) return `Page ${startPage}`
  return `Pages ${startPage}–${endPage}`
}

// Pages the statement has but no cycle and no excluded range covers. Built over a per-page flag so
// overlapping and non-contiguous spans (reunified cycles can overlap) never break the math.
export const uncoveredPageRanges = (statement: Statement): PageRange[] => {
  const totalPages = statement.numberOfPages
  if (!totalPages || totalPages < 1) return []

  // A legacy cycle with no page attribution covers pages we cannot place, so any gap we compute
  // would be a false alarm. Say nothing instead.
  const hasUnplacedSegment = (statement.segments ?? []).some(
    (segment) => segment.startPage == null || segment.endPage == null,
  )
  if (hasUnplacedSegment) return []

  const covered = new Array(totalPages + 1).fill(false)
  const markSpan = (startPage: number | null, endPage: number | null) => {
    if (startPage == null) return
    const firstPage = Math.max(1, startPage)
    const lastPage = Math.min(totalPages, endPage ?? startPage)
    for (let page = firstPage; page <= lastPage; page += 1) covered[page] = true
  }

  for (const segment of statement.segments ?? []) {
    markSpan(segment.startPage, segment.endPage)
  }
  for (const range of statement.excludedRanges ?? []) {
    markSpan(range.startPage, range.endPage)
  }

  const gaps: PageRange[] = []
  let runStart: number | null = null
  for (let page = 1; page <= totalPages; page += 1) {
    if (!covered[page] && runStart == null) runStart = page
    if (covered[page] && runStart != null) {
      gaps.push({ start: runStart, end: page - 1 })
      runStart = null
    }
  }
  if (runStart != null) gaps.push({ start: runStart, end: totalPages })
  return gaps
}

const SHORTEST_RUN_SHOWN_AS_RANGE = 3

const isPageNumber = (value: unknown): value is number =>
  Number.isInteger(value) && (value as number) > 0

const isAscendingPageList = (pages: unknown[], total: number) =>
  pages.every(
    (page, index) =>
      isPageNumber(page) &&
      page <= total &&
      (index === 0 || page > (pages[index - 1] as number)),
  )

// A shape we cannot trust says nothing instead of a false alarm. So does a list that names every
// page, since then nothing is missing.
const isTrustedPrintedPages = (
  printedPages: PrintedPages | null | undefined,
): printedPages is PrintedPages => {
  if (!printedPages || typeof printedPages !== 'object') return false
  const { total, found, held } = printedPages
  if (!isPageNumber(total) || !isPageNumber(held) || held >= total) return false
  if (!Array.isArray(found) || !isAscendingPageList(found, total)) return false
  return found.length < total
}

const pageRuns = (pages: number[]): PageRange[] =>
  pages.reduce<PageRange[]>((runs, page) => {
    const lastRun = runs[runs.length - 1]
    if (lastRun && page === lastRun.end + 1) {
      lastRun.end = page
    } else {
      runs.push({ start: page, end: page })
    }
    return runs
  }, [])

const runLabels = (run: PageRange): string[] => {
  const runLength = run.end - run.start + 1
  if (runLength >= SHORTEST_RUN_SHOWN_AS_RANGE) {
    return [`${run.start} to ${run.end}`]
  }
  return runLength === 1 ? [`${run.start}`] : [`${run.start}`, `${run.end}`]
}

const joinWithAnd = (items: string[]) =>
  items.length === 1
    ? items[0]
    : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`

const foundPagesLabel = (found: number[]) => {
  const pageWord = found.length === 1 ? 'page' : 'pages'
  return `${pageWord} ${joinWithAnd(pageRuns(found).flatMap(runLabels))}`
}

export const missingPagesLine = (
  printedPages: PrintedPages | null | undefined,
): string | null => {
  if (!isTrustedPrintedPages(printedPages)) return null
  const { total, found, held } = printedPages
  const statementSize = `This statement has ${total} pages.`
  if (found.length === held) {
    return `${statementSize} The file only has ${foundPagesLabel(found)}.`
  }
  return `${statementSize} Some of them are not in the file.`
}

// The file holds pages 1 to N of a statement that prints N + 1 pages, so only the closing page is absent.
export const isOnlyLastPageMissing = (
  printedPages: PrintedPages | null | undefined,
): boolean => {
  if (!isTrustedPrintedPages(printedPages)) return false
  const { total, found, held } = printedPages
  const holdsFirstPagesInOrder = found.every(
    (page, position) => page === position + 1,
  )
  return total === held + 1 && found.length === held && holdsFirstPagesInOrder
}

export const missingPagesChipLabel = (
  segments: StatementSegment[],
): string | null => {
  const missingCount = segments.filter(
    (segment) => missingPagesLine(segment.printedPages) != null,
  ).length
  if (missingCount === 0) return null
  return `${missingCount} ${pluralize('statement', missingCount)} missing pages`
}

export const failedSegmentPageCount = (statement: Statement): number => {
  const segments = statement.segments ?? []
  if (segments.length === 0) return statement.numberOfPages

  return segments.filter(hasStatementSegmentFailed).reduce((sum, segment) => {
    if (segment.startPage == null || segment.endPage == null) return sum
    return sum + (segment.endPage - segment.startPage + 1)
  }, 0)
}
